import React, { useCallback, useEffect, useMemo } from "react";

import { useLocalStorage } from "react-use";

import { GridIcon, ListIcon } from "../icons";
import { Button, ButtonGroup, TailSpin } from "../wombat-ui";
import ResourceProgress from "./ResourceProgress";

const AVAILABLE_GRID_CONFIGURATION = [
  "lg:grid-cols-1",
  "lg:grid-cols-2",
  "lg:grid-cols-3",
  "lg:grid-cols-4",
  "lg:grid-cols-5",
  "lg:grid-cols-6",
  "lg:grid-cols-7",
  "lg:grid-cols-8",
  "lg:grid-cols-9",
  "lg:grid-cols-10",
  "lg:grid-cols-11",
  "lg:grid-cols-12",
];

interface ListProps<T> {
  data: T[];
  renderAsList?: (data: T, index: number) => React.ReactNode;
  renderAsCard?: (data: T, index: number) => React.ReactNode;
  listOnly?: boolean;
  cardOnly?: boolean;
  cardPerRow?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  defaultDisplay?: "card" | "list";
  children?: React.ReactNode;
  id: string;
  isLoading: boolean,
  emptyMessage: string,  
}

function List<T>({
  data,
  renderAsList,
  renderAsCard,
  listOnly = false,
  cardOnly = false,
  cardPerRow = 4,
  children,
  id,
  isLoading = false,
  emptyMessage,
}: ListProps<T>) {
  const [display, setDisplay] = useLocalStorage(
    `list-display-${id}`,
    cardOnly ? "card" : "list"
  );

  /** Avoid any unwanted hidding because of a newly passed xOnly props */
  useEffect(() => {
    if (display === "card" && listOnly) setDisplay("list");
    if (display === "list" && cardOnly) setDisplay("card");
  }, [listOnly, cardOnly]);

  const setAsCard = useCallback(() => setDisplay("card"), []);
  const setAsList = useCallback(() => setDisplay("list"), []);

  return useMemo(() => {
    return (
      <div className="bg-white rounded">
        {/* Display type */}
        {children && (
          <div className="flex flex-wrap justify-between gap-2 pb-6">
            {children}
            {!listOnly && !cardOnly && (
              <ButtonGroup scale="sm">
                <Button
                  icon={GridIcon}
                  onClick={setAsCard}
                  variant={display === "card" ? "primary" : undefined}
                />
                <Button
                  icon={ListIcon}
                  onClick={setAsList}
                  variant={display === "list" ? "primary" : undefined}
                />
              </ButtonGroup>
            )}
          </div>
        )}

        <ResourceProgress
          isLoading={isLoading}
          isEmpty={data.length === 0}
          emptyMessage={emptyMessage}
        />

        {!isLoading && display === "card" && renderAsCard && data.length > 0 && (
          <div
            className={`grid ${
              AVAILABLE_GRID_CONFIGURATION[cardPerRow - 1]
            } gap-4 md:grid-cols-1`}
            id={id}
          >
            {data.map((d: T, index) => renderAsCard(d, index))}
          </div>
        )}
        {!isLoading && display === "list" && renderAsList && data.length > 0 && (
          <ul className={`divide-y divide-gray-200`} id={id}>
            {data.map((d: T, index) => renderAsList(d, index))}
          </ul>
        )}
      </div>
    );
  }, [data, renderAsList, renderAsCard, cardPerRow, display, children]);
}

export default List;
